import React, { useEffect, useRef, useState } from "react";
import { Textarea } from "./ui/textarea";

interface Props {
  envVariablesSet: any;
  handleEnvVariables: (variables?: any) => void;
}

const PLACEHOLDER_TEXT = "Paste text from an env variable file to bulk add";

const EnvVariableHandler: React.FC<Props> = ({
  envVariablesSet,
  handleEnvVariables,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [placeHolderText, setPlaceHolderText] = useState(PLACEHOLDER_TEXT);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const parseEnv = (envString: string) => {
    const env = {};
    const lines = envString.split("\n");

    for (const line of lines) {
      // Skip empty lines and comments
      if (!line || line.startsWith("#")) continue;

      // Handle multi-line values
      let [key, ...valueParts] = line.split("=");
      let value = valueParts.join("="); // Rejoin in case value contains =

      // Remove leading/trailing whitespace
      key = key.trim();
      value = value.trim();

      // Handle quoted values
      if (value.startsWith('"') && value.endsWith('"')) {
        value = value.slice(1, -1);
      }
      if (value.startsWith("'") && value.endsWith("'")) {
        value = value.slice(1, -1);
      }

      // Handle escaped characters
      value = value
        .replace(/\\n/g, "\n")
        .replace(/\\r/g, "\r")
        .replace(/\\t/g, "\t")
        .replace(/\\\\/g, "\\")
        .replace(/\\"/g, '"')
        .replace(/\\'/g, "'");

      env[key] = value;
    }

    return env;
  };

  const handlePaste = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (!value) return;

    const parsedEnv = parseEnv(value);

    // Create a filtered copy instead of mutating the prop
    const filteredEnvVariablesSet = (envVariablesSet || []).filter(
      (r) => r.key
    );

    const envVariables = Object.entries(parsedEnv).map(([key, value]) => ({
      key,
      value,
    }));
    const combined = new Map(
      filteredEnvVariablesSet.map(({ key, value }: any) => [key, value])
    );
    envVariables.forEach(({ key, value }) => combined.set(key, value));
    setPlaceHolderText("Keys added");
    setTimeout(() => {
      setPlaceHolderText(PLACEHOLDER_TEXT);
    }, 5000);

    const result = Array.from(combined, ([key, value]) => ({ key, value }));
    handleEnvVariables(result);

    // Clear the input after parsing
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const adjustTextareaHeight = (element: HTMLTextAreaElement | null) => {
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    adjustTextareaHeight(e.target);
  };

  const processInput = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handlePaste(e as any);
      setInputValue("");
    }
  };

  // Handle paste event directly on the textarea
  const handlePasteEvent = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedText = e.clipboardData?.getData("text") || "";
    const pasteEvent = {
      target: { value: pastedText },
    } as React.ChangeEvent<HTMLTextAreaElement>;

    handlePaste(pasteEvent);

    // Use setTimeout to ensure the DOM has updated
    setTimeout(() => {
      setInputValue("");
      adjustTextareaHeight(inputRef.current);
    }, 0);
  };

  // Adjust height on mount and when value changes
  useEffect(() => {
    adjustTextareaHeight(inputRef.current);
  }, [inputValue]);

  return (
    <Textarea
      ref={inputRef}
      value={inputValue}
      placeholder={placeHolderText}
      onChange={handleInput}
      onKeyDown={processInput}
      onPaste={handlePasteEvent}
      className="min-h-[46px] transition-all duration-200"
    />
  );
};

export default React.memo(EnvVariableHandler);
